import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import CourierLayout from '../CourierLayout';
import { API } from '../../../config';

const DeliveryRoutes = () => {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);

  const token =
    localStorage.getItem('courier_token') ||
    localStorage.getItem('token');

  const fetchRoutes = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${API}/api/courier/routes`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        setRoutes([]);
        return;
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        setRoutes(data);
      } else if (Array.isArray(data?.routes)) {
        setRoutes(data.routes);
      } else {
        setRoutes([]);
      }
    } catch (error) {
      console.error('Failed to fetch routes:', error);
      setRoutes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoutes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderEmptyState = () => (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3, md: 4 },
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: 3,
        textAlign: 'center',
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: '#64748b',
          fontSize: '0.95rem',
        }}
      >
        No routes found.
      </Typography>
    </Paper>
  );

  return (
    <CourierLayout>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontSize: '2rem',
            fontWeight: 700,
            lineHeight: 1.25,
            color: '#0f172a',
            mb: 1,
            textAlign: 'center',
          }}
        >
          Route Planner
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            textAlign: 'center',
            fontSize: '0.95rem',
            lineHeight: 1.5,
          }}
        >
          Review planned routes and assigned stops.
        </Typography>
      </Box>

      <Paper
        elevation={1}
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 3,
          border: '1px solid rgba(15, 23, 42, 0.08)',
          backgroundColor: '#ffffff',
        }}
      >
        {loading ? (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 3,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: '#64748b',
                fontSize: '0.95rem',
              }}
            >
              Loading routes...
            </Typography>
          </Paper>
        ) : routes.length === 0 ? (
          renderEmptyState()
        ) : (
          <Grid container spacing={2}>
            {routes.map((route) => {
              const routeId = route.route_id || route.id;
              const status = String(route.status || 'Planned');

              return (
                <Grid item xs={12} key={routeId}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: { xs: 2, md: 2.5 },
                      borderRadius: 3,
                      border: '1px solid rgba(15, 23, 42, 0.08)',
                      backgroundColor: '#ffffff',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: '#0f172a',
                            fontWeight: 700,
                            lineHeight: 1.35,
                          }}
                        >
                          Route #{routeId}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            color: '#64748b',
                            fontSize: '0.92rem',
                          }}
                        >
                          {route.total_stops || route.stops?.length || 0} stop
                          {(route.total_stops || route.stops?.length || 0) === 1 ? '' : 's'}
                        </Typography>
                      </Box>

                      <Chip
                        label={status}
                        size="small"
                        sx={{
                          backgroundColor: '#e0f2fe',
                          color: '#0369a1',
                          fontWeight: 700,
                          fontSize: '0.75rem',
                        }}
                      />
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#64748b',
                            fontWeight: 600,
                            mb: 0.5,
                          }}
                        >
                          Start
                        </Typography>

                        <Typography
                          variant="body1"
                          sx={{
                            color: '#0f172a',
                            fontWeight: 600,
                            lineHeight: 1.5,
                          }}
                        >
                          {route.start_location || route.pickup_address || 'Not provided'}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#64748b',
                            fontWeight: 600,
                            mb: 0.5,
                          }}
                        >
                          Destination
                        </Typography>

                        <Typography
                          variant="body1"
                          sx={{
                            color: '#0f172a',
                            fontWeight: 600,
                            lineHeight: 1.5,
                          }}
                        >
                          {route.end_location || route.dropoff_address || 'Not provided'}
                        </Typography>
                      </Grid>

                      {route.distance_in_miles != null && (
                        <Grid item xs={12} sm={6} md={4}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#64748b',
                              fontWeight: 600,
                              mb: 0.5,
                            }}
                          >
                            Distance
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              color: '#0f172a',
                              fontSize: '0.95rem',
                            }}
                          >
                            {route.distance_in_miles} mi
                          </Typography>
                        </Grid>
                      )}

                      {route.estimated_time && (
                        <Grid item xs={12} sm={6} md={4}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#64748b',
                              fontWeight: 600,
                              mb: 0.5,
                            }}
                          >
                            Estimated Time
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              color: '#0f172a',
                              fontSize: '0.95rem',
                            }}
                          >
                            {route.estimated_time}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>

                    <Box
                      sx={{
                        mt: 2,
                        display: 'flex',
                        justifyContent: { xs: 'stretch', sm: 'flex-end' },
                      }}
                    >
                      <Button
                        variant="outlined"
                        color="primary"
                        fullWidth={false}
                        sx={{
                          minHeight: 40,
                          textTransform: 'none',
                          fontWeight: 700,
                          borderRadius: 2,
                          width: { xs: '100%', sm: 'auto' },
                        }}
                      >
                        View Route
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Paper>
    </CourierLayout>
  );
};

export default DeliveryRoutes;